import { AnimatePresence, motion } from 'motion/react';
import { type PropsWithChildren, useEffect, useImperativeHandle } from 'react';
import ReactDOM from 'react-dom';

import { uuid } from '@/util';

import { DIALOG_CLASSNAME } from './constant';
import useDialog from './hook';
import style from './style';
import type { VDialogEmitFunction, VDialogProps } from './type';
import {
  getContainerHiddenVariants,
  getContainerVisibleVariants,
} from './util';

const VDialog = ({
  duration = 500,
  id = uuid(),
  noCloseOnBackdrop = false,
  noCloseOnEsc = false,
  transition = 'slide-y',
  visible,
  position = 'center',
  backdropOpacity = 0.75,
  dialogContainerClass = '',
  dialogMainClass = '',
  onUpdateVisible,
  onHidden,
  onHide,
  onShow,
  onShown,
  initialZIndex,
  children,
  disabledBackdrop = false,
  whiteBackdrop = false,
  ref,
}: PropsWithChildren<VDialogProps>) => {
  const emit: VDialogEmitFunction = (event, payload) => {
    switch (event) {
      case 'updateVisible':
        onUpdateVisible && onUpdateVisible(payload as boolean);
        break;
      case 'hidden':
        onHidden && onHidden(payload as any);
        break;
      case 'hide':
        onHide && onHide(payload as any);
        break;
      case 'show':
        onShow && onShow(payload as any);
        break;
      case 'shown':
        onShown && onShown(payload as any);
        break;
      default:
        break;
    }
  };

  const { state, actions, refs, events } = useDialog({
    props: {
      duration,
      id,
      noCloseOnBackdrop,
      noCloseOnEsc,
      transition,
      visible,
      position,
      dialogContainerClass,
      initialZIndex,
    },
    emit,
  });

  useImperativeHandle(ref, () => ({
    show: actions.show,
    hide: actions.hide,
    setHideTrigger: actions.setHideTrigger,
  }));

  useEffect(() => {
    visible && actions.show();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: backdropOpacity },
  };

  const containerVariants = {
    hidden: getContainerHiddenVariants({ transition }),
    visible: getContainerVisibleVariants({ transition }),
  };

  const twStyle = style({
    position,
    disabledBackdrop: disabledBackdrop,
    transition,
    whiteBackdrop,

    visible,
  });

  return ReactDOM.createPortal(
    <div className={twStyle.vDialog}>
      <AnimatePresence>
        {state.innerVisible && (
          <>
            {/* Backdrop */}
            {!disabledBackdrop && (
              <motion.div
                className={`${DIALOG_CLASSNAME.BACKDROP} ${twStyle.vDialogBackDrop}`}
                style={{ zIndex: state.zIndex }}
                initial="hidden"
                animate="visible"
                exit="hidden"
                variants={backdropVariants}
                transition={{
                  duration: duration / 1e3,
                  ease: [0.23, 1, 0.32, 1],
                }}
                key="modal-backdrop"
              />
            )}

            {/* Dialog Container */}
            <motion.div
              id={id}
              ref={refs.containerRef}
              aria-label={`v-dialog-${id}`}
              className={`${DIALOG_CLASSNAME.CONTAINER} ${twStyle.vDialogContainer} ${dialogContainerClass}`}
              style={{ zIndex: state.zIndex }}
              role="dialog"
              tabIndex={0}
              initial="hidden"
              animate="visible"
              exit="hidden"
              variants={containerVariants}
              transition={{
                duration: duration / 1e3,
                ease: [0.23, 1, 0.32, 1],
              }}
              key="modal-container"
              onClick={e => {
                !noCloseOnBackdrop &&
                  e.target === e.currentTarget &&
                  events.onBackdropClick();
              }}
              onAnimationComplete={animationState => {
                if (animationState === 'visible') {
                  const autoFocusInput =
                    refs.containerRef.current?.querySelector(
                      'input[data-autofocus]',
                    );
                  if (autoFocusInput instanceof HTMLElement) {
                    autoFocusInput.focus();
                  }
                }
              }}
            >
              <div
                className={`${DIALOG_CLASSNAME.MAIN} ${twStyle.vDialogMain} ${dialogMainClass}`}
              >
                {children}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>,
    document.body,
  );
};

export default VDialog;
